import type {
  AssignmentPointsItem,
  AssignmentPointsKind,
  AssignmentStatusValue,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../types/assignmentPointsNote';
import {
  calculateRoutineDates,
  ensureRoutineDateBuckets,
} from './assignmentPointsNoteProgress';
import { displayClassMemberName } from './classMemberNameDisplay';

export type IncompleteReportMember = {
  id: string;
  name: string;
  orderIndex: number;
  nameChoseongPositions?: number[];
};

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;

const KIND_LABEL: Record<AssignmentPointsKind, string> = {
  general: '일반',
  project: '프로젝트',
  routine: '루틴',
};

/** 일반 과제 유형 뱃지 기본색 */
export const INCOMPLETE_GENERAL_BADGE_BG = '#555555';
/** 루틴 과제 유형 뱃지색 (.cp-apn-block-badge.is-routine 과 동일) */
export const INCOMPLETE_ROUTINE_BADGE_BG = '#4f46e5';

function isIncompleteStatus(status: AssignmentStatusValue | undefined): boolean {
  const s = status ?? 'pending';
  return s === 'pending' || s === 'submitted';
}

function todayIsoLocal(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatIncompleteReportDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}(${DAY_LABELS[d.getDay()]})`;
}

export function formatIncompleteReportTimestamp(now = new Date()): string {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const h = now.getHours();
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${y}년 ${m}월 ${d}일 ${ampm} ${h12}시`;
}

/**
 * 오늘이 해당 루틴의 스케줄일(또는 커스텀 오늘 버킷)이면 오늘 ISO, 없으면 null.
 * 미완료 리포트는 오늘 루틴이 있을 때만 오늘 날짜로 판별한다.
 */
export function getRoutineTodayDate(
  item: RoutineAssignmentItem,
  now = new Date()
): string | null {
  const today = todayIsoLocal(now);
  const scheduleDates = calculateRoutineDates(
    item.startDate,
    item.dueDate,
    item.routineDays
  );
  if (scheduleDates.includes(today)) return today;
  const ensured = ensureRoutineDateBuckets(item);
  if (ensured.dateStatuses.some((b) => b.date === today)) return today;
  return null;
}

export type IncompleteStatusTarget = {
  chipKey: string;
  kind: AssignmentPointsKind;
  itemId: string;
  subTaskId?: string;
  date?: string;
  basePoints: number;
};

export type IncompleteAssignmentCard = {
  itemId: string;
  kind: AssignmentPointsKind;
  kindLabel: string;
  title: string;
  dateLabel: string;
  sortDate: string;
  /** 유형 뱃지 배경색. 일반은 null → 기본색 */
  accentColor: string | null;
  /** 과제 커버 이미지(노트 coverImages 라이브러리 id) */
  coverImageId?: string;
  /** 상태 변경에 쓰는 칩 타깃(프로젝트는 미완료 하위과제 전부) */
  targets: IncompleteStatusTarget[];
};

export type IncompleteByMemberRow = {
  memberId: string;
  orderIndex: number;
  memberName: string;
  cards: IncompleteAssignmentCard[];
};

export type IncompleteByAssignmentRow = {
  itemId: string;
  kind: AssignmentPointsKind;
  kindLabel: string;
  title: string;
  dateLabel: string;
  sortDate: string;
  accentColor: string | null;
  members: Array<{ memberId: string; orderIndex: number; memberName: string }>;
};

function memberIncompleteOnGeneral(
  item: GeneralAssignmentItem,
  memberId: string
): boolean {
  if (!item.assignedMemberIds.includes(memberId)) return false;
  const st = item.studentStatuses.find((s) => s.studentId === memberId);
  return isIncompleteStatus(st?.status);
}

function memberIncompleteOnProject(
  item: ProjectAssignmentItem,
  memberId: string
): boolean {
  if (!item.assignedMemberIds.includes(memberId)) return false;
  if (item.subTasks.length === 0) return isIncompleteStatus(undefined);
  for (const sub of item.subTasks) {
    const st = item.studentStatusesBySubTask[`${sub.id}:${memberId}`];
    if (isIncompleteStatus(st?.status)) return true;
  }
  return false;
}

function memberIncompleteOnRoutine(
  item: RoutineAssignmentItem,
  memberId: string,
  now = new Date()
): boolean {
  const today = getRoutineTodayDate(item, now);
  if (!today) return false;
  if (!item.assignedMemberIds.includes(memberId)) return false;
  const ensured = ensureRoutineDateBuckets(item);
  const bucket = ensured.dateStatuses.find((b) => b.date === today);
  const st = bucket?.studentStatuses.find((s) => s.studentId === memberId);
  return isIncompleteStatus(st?.status);
}

export function memberHasIncompleteOnItem(
  item: AssignmentPointsItem,
  memberId: string,
  now = new Date()
): boolean {
  if (item.kind === 'general') return memberIncompleteOnGeneral(item, memberId);
  if (item.kind === 'project') return memberIncompleteOnProject(item, memberId);
  return memberIncompleteOnRoutine(item, memberId, now);
}

function resolveAccentColor(item: AssignmentPointsItem): string | null {
  if (item.kind === 'project') {
    return (item.color || '').trim() || '#FC5555';
  }
  if (item.kind === 'routine') {
    return INCOMPLETE_ROUTINE_BADGE_BG;
  }
  return null;
}

function buildStatusTargets(
  item: AssignmentPointsItem,
  memberId: string,
  now = new Date()
): IncompleteStatusTarget[] {
  if (item.kind === 'general') {
    if (!memberIncompleteOnGeneral(item, memberId)) return [];
    return [
      {
        chipKey: `g:${item.id}`,
        kind: 'general',
        itemId: item.id,
        basePoints: item.basePoints,
      },
    ];
  }
  if (item.kind === 'routine') {
    const today = getRoutineTodayDate(item, now);
    if (!today || !memberIncompleteOnRoutine(item, memberId, now)) return [];
    return [
      {
        chipKey: `r:${item.id}:${today}`,
        kind: 'routine',
        itemId: item.id,
        date: today,
        basePoints: item.basePoints,
      },
    ];
  }
  // project: 멤버 기준 미완료 하위과제만
  if (!item.assignedMemberIds.includes(memberId)) return [];
  if (item.subTasks.length === 0) return [];
  const targets: IncompleteStatusTarget[] = [];
  for (const sub of item.subTasks) {
    const st = item.studentStatusesBySubTask[`${sub.id}:${memberId}`];
    if (!isIncompleteStatus(st?.status)) continue;
    targets.push({
      chipKey: `p:${item.id}:${sub.id}`,
      kind: 'project',
      itemId: item.id,
      subTaskId: sub.id,
      basePoints: sub.points,
    });
  }
  return targets;
}

function toCard(
  item: AssignmentPointsItem,
  now = new Date(),
  memberId?: string
): IncompleteAssignmentCard {
  let dateLabel = formatIncompleteReportDate(item.dueDate);
  let sortDate = item.dueDate || '';
  if (item.kind === 'routine') {
    const today = getRoutineTodayDate(item, now);
    if (today) {
      dateLabel = formatIncompleteReportDate(today);
      sortDate = today;
    }
  }
  return {
    itemId: item.id,
    kind: item.kind,
    kindLabel: KIND_LABEL[item.kind],
    title: item.title?.trim() || '(제목 없음)',
    dateLabel,
    sortDate,
    accentColor: resolveAccentColor(item),
    coverImageId: item.coverImageId,
    targets: memberId ? buildStatusTargets(item, memberId, now) : [],
  };
}

/** 선택 그룹 멤버 × 전체 과제 기준 — 멤버별 미완료 과제 */
export function buildIncompleteByMemberRows(
  items: AssignmentPointsItem[],
  members: IncompleteReportMember[],
  now = new Date()
): IncompleteByMemberRow[] {
  const memberIds = new Set(members.map((m) => m.id));
  const scopedItems = items.filter((it) =>
    it.assignedMemberIds.some((id) => memberIds.has(id))
  );
  const rows: IncompleteByMemberRow[] = [];
  for (const m of members) {
    const cards = scopedItems
      .filter((it) => memberHasIncompleteOnItem(it, m.id, now))
      .map((it) => toCard(it, now, m.id))
      .sort(
        (a, b) =>
          a.sortDate.localeCompare(b.sortDate) ||
          a.title.localeCompare(b.title, 'ko')
      );
    if (cards.length === 0) continue;
    rows.push({
      memberId: m.id,
      orderIndex: m.orderIndex,
      memberName: displayClassMemberName(m).trim() || '(이름 없음)',
      cards,
    });
  }
  return rows.sort((a, b) => a.orderIndex - b.orderIndex);
}

/** 선택 그룹 멤버 × 전체 과제 기준 — 과제별 미완료 멤버 */
export function buildIncompleteByAssignmentRows(
  items: AssignmentPointsItem[],
  members: IncompleteReportMember[],
  now = new Date()
): IncompleteByAssignmentRow[] {
  const memberById = new Map(members.map((m) => [m.id, m]));
  const rows: IncompleteByAssignmentRow[] = [];
  for (const item of items) {
    // 루틴: 오늘 스케줄이 없으면 리포트에서 제외
    if (item.kind === 'routine' && !getRoutineTodayDate(item, now)) continue;

    const incompleteMembers = item.assignedMemberIds
      .map((id) => memberById.get(id))
      .filter((m): m is IncompleteReportMember => !!m)
      .filter((m) => memberHasIncompleteOnItem(item, m.id, now))
      .map((m) => ({
        memberId: m.id,
        orderIndex: m.orderIndex,
        memberName: displayClassMemberName(m).trim() || '(이름 없음)',
      }))
      .sort((a, b) => a.orderIndex - b.orderIndex);
    if (incompleteMembers.length === 0) continue;
    const card = toCard(item, now);
    rows.push({
      itemId: item.id,
      kind: card.kind,
      kindLabel: card.kindLabel,
      title: card.title,
      dateLabel: card.dateLabel,
      sortDate: card.sortDate,
      accentColor: card.accentColor,
      members: incompleteMembers,
    });
  }
  return rows.sort(
    (a, b) =>
      a.sortDate.localeCompare(b.sortDate) || a.title.localeCompare(b.title, 'ko')
  );
}

export function incompleteKindBadgeStyle(
  accentColor: string | null | undefined
): { background: string } {
  return {
    background: accentColor?.trim() || INCOMPLETE_GENERAL_BADGE_BG,
  };
}
